import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { seedAPI, configAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_caredata-suite/artifacts/8itk90ub_55315630-dcf1-43bd-9c0d-4f1818f5e217.jpeg";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [clinicName, setClinicName] = useState('Apex Medical Group');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    // Fetch clinic config
    configAPI.getClinic().then(res => {
      setClinicName(res.data.name);
    }).catch(() => {});
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await seedAPI.seed();
      if (response.data.credentials) {
        setSeeded(true);
        toast.success('Sample data created! Use the credentials below to login.');
      } else {
        toast.info(response.data.message);
        setSeeded(true);
      }
    } catch (err) {
      toast.error('Failed to seed data');
    }
    setSeeding(false);
  };

  const demoCredentials = [
    { role: 'Admin', email: 'admin@clinic.com', password: 'admin123' },
    { role: 'Physician', email: 'physician@clinic.com', password: 'physician123' },
    { role: 'Nurse', email: 'nurse@clinic.com', password: 'nurse123' },
    { role: 'Clerk', email: 'clerk@clinic.com', password: 'clerk123' },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="login-page">
      <div className="w-full max-w-md space-y-6 animate-fadeIn">
        {/* Logo */}
        <div className="text-center">
          <img 
            src={LOGO_URL} 
            alt={clinicName} 
            className="w-24 h-24 mx-auto mb-4 rounded-2xl object-contain"
          />
          <h1 className="text-3xl font-bold tracking-tight">{clinicName}</h1>
          <p className="text-muted-foreground mt-2">Electronic Medical Records System</p>
        </div>

        {/* Login Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>Enter your credentials to access the system</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {authError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{authError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@clinic.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-btn"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign in
              </Button>
            </form>

            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                Create one
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Event Quote Request Link */}
        <Card className="border-dashed">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-muted-foreground mb-2">Need event medical services?</p>
            <Link to="/event-quote" className="text-primary hover:underline font-medium" data-testid="event-quote-link">
              Request a Quote →
            </Link>
          </CardContent>
        </Card>

        {/* Seed Data Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Demo Mode</CardTitle>
            <CardDescription>Load sample data to explore the system</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleSeedData}
              disabled={seeding || seeded}
              data-testid="seed-data-btn"
            >
              {seeding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {seeded && <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />}
              {seeded ? 'Sample Data Loaded' : 'Load Sample Data'}
            </Button>

            {seeded && (
              <div className="space-y-2 animate-fadeIn">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Demo Credentials</p>
                <div className="grid gap-2">
                  {demoCredentials.map((cred) => (
                    <button
                      key={cred.role}
                      type="button"
                      className="flex items-center justify-between p-2 rounded-md bg-muted/50 hover:bg-muted text-left text-sm transition-colors"
                      onClick={() => {
                        setEmail(cred.email);
                        setPassword(cred.password);
                      }}
                      data-testid={`demo-${cred.role.toLowerCase()}`}
                    >
                      <span className="font-medium">{cred.role}</span>
                      <span className="text-muted-foreground text-xs">{cred.email}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
